import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Spin, message } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { Amount } from "fin-web-components";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { H5SubPageShell } from "../../components/common/H5SubPageShell";
import "../../components/common/H5SubPageShell.css";
import { StoreOrderApi } from "../../customer_account/StoreOrderApi";
import type { AppMyStoreOrderRow } from "../../customer_account/StoreOrderType";
import {
  STORE_ORDER_PAY_DEADLINE_MS,
  formatPayCountdown,
  isStoreOrderPaid,
  parseStoreOrderCreatedAtMs,
  resolveH5StoreOrderListPath,
  resolveH5StoreOrderServicePath,
} from "../../customer_account/storeOrderDetailUtils";
import { formatOrderSpecLabel, isUnpaidStoreOrder } from "../../customer_account/storeOrderUtils";
import { formatApiDateTime } from "../../utils/Backend";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";
import { useRequireLoginRedirect } from "../../utils/useRequireLoginRedirect";
import { WeworkServiceApi } from "../../wework_service/WeworkServiceApi";
import { WeworkServiceModal } from "../../wework_service/WeworkServiceModal";
import type { WeworkServiceCardView } from "../../wework_service/WeworkServiceType";
import "./SpaceMineOrderDetailStyle.css";

function MetaRow(props: { label: string; value: string; mono?: boolean; copyable?: boolean }) {
  const { label, value, mono, copyable } = props;

  const onCopy = () => {
    void copyTextToClipboard(value).then((ok) => {
      if (ok) {
        message.success("已复制订单号");
        return;
      }
      message.error("复制失败，请重试");
    });
  };

  return (
    <div className="h5-order-detail__meta-row">
      <span className="h5-order-detail__meta-label">{label}</span>
      {copyable ? (
        <span className="h5-order-detail__meta-value-wrap">
          <span
            className={
              mono
                ? "h5-order-detail__meta-value h5-order-detail__meta-value--mono"
                : "h5-order-detail__meta-value"
            }
          >
            {value}
          </span>
          <button
            type="button"
            className="h5-order-detail__copy"
            aria-label="复制订单号"
            title="复制"
            onClick={onCopy}
          >
            <CopyOutlined />
          </button>
        </span>
      ) : (
        <span
          className={
            mono
              ? "h5-order-detail__meta-value h5-order-detail__meta-value--mono"
              : "h5-order-detail__meta-value"
          }
        >
          {value}
        </span>
      )}
    </div>
  );
}

function statusBadgeClass(code: string): string {
  if (code === "unpaid") {
    return "h5-order-detail__status-badge h5-order-detail__status-badge--unpaid";
  }
  if (code === "cancelled") {
    return "h5-order-detail__status-badge h5-order-detail__status-badge--muted";
  }
  return "h5-order-detail__status-badge h5-order-detail__status-badge--paid";
}

function statusSectionClass(code: string): string {
  if (code === "unpaid") {
    return "h5-order-detail__status h5-order-detail__status--unpaid";
  }
  if (isStoreOrderPaid({ displayStatus: code })) {
    return "h5-order-detail__status h5-order-detail__status--paid";
  }
  return "h5-order-detail__status";
}

export const SpaceMineOrderDetailPage = observer(() => {
  const { spaceId, orderId } = useParams<{ spaceId: string; orderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const canView = useRequireLoginRedirect();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<AppMyStoreOrderRow | null>(null);
  const [paid, setPaid] = useState(false);
  const [payRemainMs, setPayRemainMs] = useState(STORE_ORDER_PAY_DEADLINE_MS);
  const [payGuideOpen, setPayGuideOpen] = useState(false);
  const [payGuideLoading, setPayGuideLoading] = useState(false);
  const [payGuideCard, setPayGuideCard] = useState<WeworkServiceCardView | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const ctxSpaceId = spaceId?.trim() || "";
  const orderSpaceId = searchParams.get("orderSpaceId")?.trim() || ctxSpaceId;

  const loadDetail = useCallback(async () => {
    if (!canView || !orderSpaceId || !orderId) {
      return;
    }
    setLoading(true);
    const res = await StoreOrderApi.getMyOrderDetail({ spaceId: orderSpaceId, orderId });
    setLoading(false);
    if (typeof res === "string") {
      return;
    }
    const row = res.order;
    setOrder(row);
    setPaid(isStoreOrderPaid(row));
  }, [canView, orderId, orderSpaceId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (!orderSpaceId || !orderId || paid || !order || !isUnpaidStoreOrder(order)) {
      return;
    }
    pollRef.current = setInterval(() => {
      void (async () => {
        const res = await StoreOrderApi.queryPayStatus({ spaceId: orderSpaceId, orderId });
        if (typeof res === "string") {
          return;
        }
        if (res.paid) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
          }
          setPaid(true);
          setOrder((prev) =>
            prev
              ? {
                  ...prev,
                  displayStatus: "paid_active",
                  displayStatusLabel: "已支付",
                  payTime: formatApiDateTime(res.payTime),
                }
              : prev
          );
        }
      })();
    }, 1500);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [order, orderId, orderSpaceId, paid]);

  useEffect(() => {
    if (paid || !order || !isUnpaidStoreOrder(order)) {
      if (cdRef.current) {
        clearInterval(cdRef.current);
      }
      return;
    }
    const createdMs = parseStoreOrderCreatedAtMs(order.createdAt);
    if (createdMs == null) {
      return;
    }
    const deadlineMs = createdMs + STORE_ORDER_PAY_DEADLINE_MS;
    const tick = () => {
      setPayRemainMs(Math.max(0, deadlineMs - Date.now()));
    };
    tick();
    cdRef.current = setInterval(tick, 1000);
    return () => {
      if (cdRef.current) {
        clearInterval(cdRef.current);
      }
    };
  }, [paid, order]);

  const openPayGuide = useCallback(async () => {
    if (!orderSpaceId) {
      return;
    }
    setPayGuideLoading(true);
    try {
      const res = await WeworkServiceApi.getLiveCode({ spaceId: orderSpaceId });
      if (!res?.card) {
        message.warning("暂未配置企业微信客服，请联系空间管理员");
        return;
      }
      setPayGuideCard(res.card);
      setPayGuideOpen(true);
    } finally {
      setPayGuideLoading(false);
    }
  }, [orderSpaceId]);

  const confirmCancelOrder = useCallback(() => {
    if (!orderSpaceId || !orderId) {
      return;
    }
    Modal.confirm({
      title: "取消订单",
      content: "确定取消该订单吗？取消后需重新下单。",
      okText: "确认取消",
      cancelText: "暂不取消",
      centered: true,
      okButtonProps: { style: { minWidth: 120, borderRadius: 0 } },
      cancelButtonProps: { style: { minWidth: 120, borderRadius: 0 } },
      onOk: async () => {
        setCancelling(true);
        try {
          const res = await StoreOrderApi.cancelOrder({ spaceId: orderSpaceId, orderId });
          if (typeof res === "string") {
            return;
          }
          message.success("订单已取消");
          if (pollRef.current) {
            clearInterval(pollRef.current);
          }
          if (cdRef.current) {
            clearInterval(cdRef.current);
          }
          navigate(resolveH5StoreOrderListPath(ctxSpaceId));
        } finally {
          setCancelling(false);
        }
      },
    });
  }, [ctxSpaceId, navigate, orderId, orderSpaceId]);

  const goService = () => {
    if (!order) {
      return;
    }
    navigate(resolveH5StoreOrderServicePath(order));
  };

  const backTo = ctxSpaceId ? resolveH5StoreOrderListPath(ctxSpaceId) : undefined;
  const unpaid = order != null && isUnpaidStoreOrder(order) && !paid;
  const payExpired = unpaid && payRemainMs <= 0;
  const posterUrl = resolveAppMediaUrl(order?.productPosterUrl?.trim() || null);

  return (
    <H5SubPageShell title="订单详情" backTo={backTo}>
      <Spin spinning={loading}>
        {order ? (
          <div className="h5-order-detail">
            <section className={statusSectionClass(order.displayStatus)}>
              <div className="h5-order-detail__status-row">
                <p className="h5-order-detail__status-label">
                  {unpaid ? "等待支付" : paid ? "支付成功" : order.displayStatusLabel || "订单详情"}
                </p>
                <span className={statusBadgeClass(order.displayStatus)}>
                  {order.displayStatusLabel || "—"}
                </span>
              </div>
              {unpaid ? (
                <>
                  <p className="h5-order-detail__status-hint">请在 30 分钟内完成支付，超时订单将自动关闭</p>
                  <span className="h5-order-detail__countdown">剩余 {formatPayCountdown(payRemainMs)}</span>
                </>
              ) : paid ? (
                <p className="h5-order-detail__status-hint">权益已生效，可前往产品页使用完整服务</p>
              ) : null}
            </section>

            <section className="h5-order-detail__section">
              <div className="h5-order-detail__product">
                <div className="h5-order-detail__poster">
                  {posterUrl ? (
                    <img src={posterUrl} alt="" className="h5-order-detail__poster-img" />
                  ) : (
                    <span className="h5-order-detail__poster-fallback" aria-hidden>
                      ¥
                    </span>
                  )}
                </div>
                <div className="h5-order-detail__product-main">
                  <p className="h5-order-detail__product-name">{order.productName?.trim() || "—"}</p>
                  <p className="h5-order-detail__product-spec">{formatOrderSpecLabel(order)}</p>
                  <p className="h5-order-detail__product-space">{order.spaceName?.trim() || "—"}</p>
                </div>
              </div>
            </section>

            <section className="h5-order-detail__section">
              <p className="h5-order-detail__section-title">订单信息</p>
              <div className="h5-order-detail__meta-list">
                <MetaRow label="订单号" value={order.orderId} mono copyable />
                <MetaRow label="下单时间" value={formatApiDateTime(order.createdAt) || "—"} mono />
                {paid && order.payTime ? (
                  <MetaRow label="支付时间" value={formatApiDateTime(order.payTime)} mono />
                ) : null}
              </div>
            </section>

            <section className="h5-order-detail__section">
              <p className="h5-order-detail__section-title">金额明细</p>
              <div className="h5-order-detail__amount-list">
                <div className="h5-order-detail__amount-row">
                  <span className="h5-order-detail__amount-label">商品价格</span>
                  <span className="h5-order-detail__amount-value">
                    <Amount value={order.productPriceYuan} fontSize={14} fontWeight={400} />
                  </span>
                </div>
                {order.orderType === "upgrade" &&
                order.upgradeCreditYuan != null &&
                order.upgradeCreditYuan > 0 ? (
                  <div className="h5-order-detail__amount-row">
                    <span className="h5-order-detail__amount-label">
                      原权益抵扣
                      {order.upgradeFromProductName?.trim()
                        ? `（${order.upgradeFromProductName.trim()}）`
                        : ""}
                    </span>
                    <span className="h5-order-detail__amount-value h5-order-detail__amount-value--credit">
                      −<Amount value={order.upgradeCreditYuan} fontSize={14} fontWeight={400} />
                    </span>
                  </div>
                ) : null}
                <div className="h5-order-detail__amount-row h5-order-detail__amount-row--total">
                  <span className="h5-order-detail__amount-label">{paid ? "实付金额" : "应付金额"}</span>
                  <span className="h5-order-detail__amount-value h5-order-detail__amount-value--pay">
                    <Amount value={order.payAmountYuan} fontSize={18} fontWeight={400} />
                  </span>
                </div>
              </div>
            </section>

            {unpaid ? (
              <div className="h5-order-detail__links">
                <button
                  type="button"
                  className="h5-order-detail__link"
                  onClick={() => {
                    if (backTo) {
                      navigate(backTo);
                      return;
                    }
                    navigate(-1);
                  }}
                >
                  前往订单列表
                </button>
                <button
                  type="button"
                  className="h5-order-detail__link h5-order-detail__link--danger"
                  disabled={cancelling}
                  onClick={confirmCancelOrder}
                >
                  取消订单
                </button>
              </div>
            ) : null}

            {unpaid ? (
              <div className="h5-order-detail__foot">
                <button
                  type="button"
                  className="h5-order-detail__foot-btn"
                  disabled={payExpired || payGuideLoading}
                  onClick={() => void openPayGuide()}
                >
                  {payGuideLoading ? "加载中…" : `立即支付 ${formatPayCountdown(payRemainMs)}`}
                </button>
              </div>
            ) : paid ? (
              <div className="h5-order-detail__foot">
                <button type="button" className="h5-order-detail__foot-btn h5-order-detail__foot-btn--service" onClick={goService}>
                  进入服务
                </button>
              </div>
            ) : null}
          </div>
        ) : !loading ? (
          <p className="h5-sub-page__empty">订单不存在或暂不可访问</p>
        ) : null}
      </Spin>

      <WeworkServiceModal
        open={payGuideOpen}
        title="支付提示"
        content="暂不支持在线支付，请联系您的服务专员"
        card={payGuideCard}
        onClose={() => setPayGuideOpen(false)}
      />
    </H5SubPageShell>
  );
});

export default SpaceMineOrderDetailPage;
